import React from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";
import {
  ISuperficie,
  ISelectOption,
  SITUACAO_OPTIONS,
  STATUS_OPTIONS,
  VOLTAGEM_OPTIONS,
  TERMINAL_OPTIONS,
  grupoTemDescricao,
  GRUPO_TELA_RETRATIL,
  GRUPO_ESTEIRA_MONTADA,
} from "../types/superficie.types";
import { InputDecimalPtBr } from "../../components/InputDecimalPtBr";

// Estilos customizados específicos para esta página - SEM BORDA
const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f0f0f0",
    borderRadius: 0,
    minHeight: 56,
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#3C90AD",
    fontWeight: 500,
    fontSize: "0.8rem",
    "&.Mui-focused": {
      color: "#3C90AD",
    },
  },
  "& .MuiInputBase-input": {
    padding: "20px 14px 8px 14px",
  },
};

const selectSx = {
  backgroundColor: "#f0f0f0",
  borderRadius: 0,
  minHeight: 56,
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select": {
    padding: "20px 14px 8px 14px",
  },
};

const labelSx = {
  color: "#3C90AD",
  fontWeight: 500,
  fontSize: "0.8rem",
  "&.Mui-focused": {
    color: "#3C90AD",
  },
};

interface TabGeralProps {
  superficie: Partial<ISuperficie>;
  onChange: (campo: keyof ISuperficie, valor: any) => void;
  referencias: ISelectOption[];
  linhas: ISelectOption[];
  grupos: ISelectOption[];
  cores: ISelectOption[];
  unidadesValorizacao: ISelectOption[];
  tiposTecido: ISelectOption[];
  coresTecido: ISelectOption[];
  // FASE 05 — 4ª validação/Frente 9 2026-07-30: sinaliza duplicidade em tempo real no campo.
  codigoDuplicado?: boolean;
  onOpenMidias: () => void;
}

export const TabGeral: React.FC<TabGeralProps> = ({
  superficie,
  onChange,
  referencias,
  linhas,
  grupos,
  cores,
  unidadesValorizacao,
  tiposTecido,
  coresTecido,
  codigoDuplicado,
  onOpenMidias,
}) => {
  // FASE 05 — 6ª validação/PO 2026-08-06: "Preço Variável" e a detecção de M² saíram do formulário
  // de Superfície. Os campos condicionais são decididos pela DESCRIÇÃO do grupo selecionado no
  // Cadastro de Grupos (case-insensitive).
  const ehTelaRetratil = grupoTemDescricao(grupos, superficie.idGrupo, GRUPO_TELA_RETRATIL);
  const ehEsteiraMontada = grupoTemDescricao(grupos, superficie.idGrupo, GRUPO_ESTEIRA_MONTADA);

  return (
    <Box>
      {/* FASE 05 — 6ª validação/PO 2026-08-11: Situação numa linha ACIMA do container principal,
          para que Código | Código interno | Status fiquem alinhados ao topo do quadro da imagem. */}
      <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="flex-end">
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel sx={labelSx}>Situação</InputLabel>
            <Select
              value={superficie.idSituacao || 1}
              label="Situação"
              disabled
              onChange={(e) => onChange("idSituacao", e.target.value)}
              sx={selectSx}
            >
              {SITUACAO_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      {/* Container principal com Imagem à esquerda e campos à direita */}
      <Grid container spacing={3}>
        {/* Coluna Esquerda - Imagem do Produto */}
        <Grid item xs={12} md={3}>
          {/* Container da Imagem - COM BORDA CINZA */}
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              backgroundColor: "#fff",
              p: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#333", mb: 2, fontWeight: 500 }}
            >
              Imagem do produto
            </Typography>
            
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 180,
                backgroundColor: "#fff",
              }}
            >
              {superficie.imagemUrl ? (
                <img
                  src={superficie.imagemUrl}
                  alt="Imagem do produto"
                  style={{ maxWidth: "100%", maxHeight: 180, objectFit: "contain" }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma imagem
                </Typography>
              )}
            </Box>

            {superficie.imagemNomeArquivo && (
              <Typography
                variant="caption"
                sx={{ color: "#999", textAlign: "center", display: "block", mt: 2 }}
              >
                {superficie.imagemNomeArquivo}
              </Typography>
            )}
          </Box>

          {/* Botão FORA do container da imagem */}
          <Button
            variant="contained"
            fullWidth
            onClick={onOpenMidias}
            sx={{
              backgroundColor: "#3C90AD",
              "&:hover": { backgroundColor: "#2d6f87" },
              textTransform: "none",
              py: 1.2,
            }}
          >
            GERENCIAMENTO DE MÍDIAS
          </Button>
        </Grid>

        {/* Coluna Direita - Campos do Formulário */}
        <Grid item xs={12} md={9}>
          {/* FASE 05 — 4ª validação/Frente 9 2026-07-30: ordem do mockup (image10) — Código | Código
              interno, Descrição em linha própria, Referência | Linha | Grupo, Cor | Un. Valorização. */}
          {/* Linha: Código, Código interno, Status */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Código"
                value={superficie.codigo || ""}
                onChange={(e) => onChange("codigo", e.target.value.toUpperCase())}
                required
                inputProps={{ maxLength: 50 }}
                error={codigoDuplicado}
                helperText={codigoDuplicado ? "Código já existe" : undefined}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Código interno"
                value={superficie.codigoInterno || ""}
                onChange={(e) => onChange("codigoInterno", e.target.value)}
                inputProps={{ maxLength: 50 }}
                sx={inputSx}
              />
            </Grid>
            {/* FASE 05 — 6ª validação/PO 2026-08-07 (correção de rumo): Status na 3ª coluna,
                alinhado com Código/Código interno (Situação fica na linha acima). */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={labelSx}>Status</InputLabel>
                <Select
                  value={superficie.idStatus || 1}
                  label="Status"
                  onChange={(e) => onChange("idStatus", e.target.value)}
                  sx={selectSx}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Linha: Descrição */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={superficie.descricao || ""}
                onChange={(e) => onChange("descricao", e.target.value)}
                required
                inputProps={{ maxLength: 200 }}
                sx={inputSx}
              />
            </Grid>
          </Grid>

          {/* Linha: Referência, Linha, Grupo */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              {/* FASE 05 — 4ª validação/Frente 9 2026-07-30 (P2): Referência e Linha permanecem
                  obrigatórias — asterisco sinalizando. */}
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Referência (fornecedor do material)</InputLabel>
                <Select
                  value={superficie.idReferencia || ""}
                  label="Referência (fornecedor do material)"
                  onChange={(e) => onChange("idReferencia", e.target.value || undefined)}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {referencias.map((ref) => (
                    <MenuItem key={ref.id} value={ref.id}>
                      {ref.nome || ref.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Linha</InputLabel>
                <Select
                  value={superficie.idLinha || ""}
                  label="Linha"
                  onChange={(e) => onChange("idLinha", e.target.value || undefined)}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {linhas.map((linha) => (
                    <MenuItem key={linha.id} value={linha.id}>
                      {linha.nome || linha.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {/* FASE 05 — 4ª validação/Frente 9 2026-07-30: "Categoria" (lista fixa) virou "Grupo",
                  com as opções vindas do Cadastro de Grupos (flag Superfície). */}
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Grupo</InputLabel>
                <Select
                  value={superficie.idGrupo && grupos.some((g) => g.id === superficie.idGrupo) ? superficie.idGrupo : ""}
                  label="Grupo"
                  onChange={(e) => {
                    const novoIdGrupo = e.target.value === "" ? undefined : Number(e.target.value);
                    onChange("idGrupo", novoIdGrupo);
                    // Limpa os condicionais que não pertencem ao novo grupo (descrição decide).
                    const novoEhTela = grupoTemDescricao(grupos, novoIdGrupo, GRUPO_TELA_RETRATIL);
                    const novoEhEsteira = grupoTemDescricao(grupos, novoIdGrupo, GRUPO_ESTEIRA_MONTADA);
                    if (!novoEhTela) {
                      onChange("idTipoTecido", undefined);
                      onChange("idCorTecido", undefined);
                      onChange("voltagem", undefined);
                      onChange("larguraMinima", undefined);
                      onChange("larguraMaxima", undefined);
                      onChange("alturaMinima", undefined);
                      onChange("alturaMaxima", undefined);
                    }
                    if (!novoEhEsteira) {
                      onChange("peso", undefined);
                      onChange("passo", undefined);
                      onChange("terminal", undefined);
                    }
                  }}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {grupos.map((grupo) => (
                    <MenuItem key={grupo.id} value={grupo.id}>
                      {grupo.nome || grupo.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Linha: Cor, Un. Valorização */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Cor</InputLabel>
                <Select
                  value={superficie.idCor || ""}
                  label="Cor"
                  onChange={(e) => onChange("idCor", e.target.value || undefined)}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {cores.map((cor) => (
                    <MenuItem key={cor.id} value={cor.id}>
                      {cor.nome || cor.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Un. Valorização</InputLabel>
                <Select
                  value={superficie.idUnidadeValorizacao || ""}
                  label="Un. Valorização"
                  onChange={(e) => onChange("idUnidadeValorizacao", e.target.value)}
                  required
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {unidadesValorizacao.map((unidade) => (
                    <MenuItem key={unidade.id} value={unidade.id}>
                      {unidade.nome || unidade.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Campos Condicionais - Grupo TELA RETRÁTIL */}
          {ehTelaRetratil && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth required>
                    <InputLabel sx={labelSx}>Tipo tecido</InputLabel>
                    <Select
                      value={superficie.idTipoTecido || ""}
                      label="Tipo tecido"
                      onChange={(e) => onChange("idTipoTecido", e.target.value || undefined)}
                      required
                      sx={selectSx}
                    >
                      <MenuItem value="">
                        <em>Selecione...</em>
                      </MenuItem>
                      {tiposTecido.map((tipo) => (
                        <MenuItem key={tipo.id} value={tipo.id}>
                          {tipo.nome || tipo.descricao}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  {/* FASE 05 — 6ª validação/PO 2026-08-06 (correção): "Cor do tecido" volta ao form da
                      TELA RETRÁTIL; grava em IdCorTecido (coluna criada por SCHEMA_Superficie_IdCorTecido). */}
                  <FormControl fullWidth>
                    <InputLabel sx={labelSx}>Cor do tecido</InputLabel>
                    <Select
                      value={superficie.idCorTecido || ""}
                      label="Cor do tecido"
                      onChange={(e) => onChange("idCorTecido", e.target.value || undefined)}
                      sx={selectSx}
                    >
                      <MenuItem value="">
                        <em>Selecione...</em>
                      </MenuItem>
                      {coresTecido.map((cor) => (
                        <MenuItem key={cor.id} value={cor.id}>
                          {cor.nome || cor.descricao}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  {/* FASE 05 — 6ª validação/PO 2026-08-06: Voltagem (110V=1/220V=2/bivolt=3) grava
                      na coluna numérica Voltagem. */}
                  <FormControl fullWidth>
                    <InputLabel sx={labelSx}>Voltagem</InputLabel>
                    <Select
                      value={VOLTAGEM_OPTIONS.some((o) => o.id === superficie.voltagem) ? superficie.voltagem : ""}
                      label="Voltagem"
                      onChange={(e) => onChange("voltagem", e.target.value || undefined)}
                      sx={selectSx}
                    >
                      <MenuItem value="">
                        <em>Selecione...</em>
                      </MenuItem>
                      {VOLTAGEM_OPTIONS.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {/* FASE 05 — 4ª validação/Frente 9 2026-07-30 (rev. deploy): dimensões mín/máx
                  da tela em milímetros INTEIROS — as colunas LarguraMinima/... já existiam no
                  banco como INT (modelo legado, 5.784 registros); decimal quebraria o par
                  legado×novo. */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Largura Mínima"
                    value={superficie.larguraMinima ?? ""}
                    onChange={(e) => onChange("larguraMinima", e.target.value === "" ? undefined : Math.max(0, parseInt(e.target.value) || 0))}
                    required
                    inputProps={{ min: 0, step: 1 }}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Largura Máxima"
                    value={superficie.larguraMaxima ?? ""}
                    onChange={(e) => onChange("larguraMaxima", e.target.value === "" ? undefined : Math.max(0, parseInt(e.target.value) || 0))}
                    required
                    inputProps={{ min: 0, step: 1 }}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Altura Mínima"
                    value={superficie.alturaMinima ?? ""}
                    onChange={(e) => onChange("alturaMinima", e.target.value === "" ? undefined : Math.max(0, parseInt(e.target.value) || 0))}
                    required
                    inputProps={{ min: 0, step: 1 }}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Altura Máxima"
                    value={superficie.alturaMaxima ?? ""}
                    onChange={(e) => onChange("alturaMaxima", e.target.value === "" ? undefined : Math.max(0, parseInt(e.target.value) || 0))}
                    required
                    inputProps={{ min: 0, step: 1 }}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </>
          )}

          {/* Campos Condicionais - Grupo ESTEIRA MONTADA (mockup image12) */}
          {ehEsteiraMontada && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                  {/* FASE 05 — 4ª validação/Frente 9 2026-07-30: decimal pt-BR igual ao Peso do Perfil;
                      Box intermediário porque o Tooltip exige um filho que aceite ref. */}
                  <Tooltip title="Informe o peso aplicável para esteira.">
                    <Box>
                      <InputDecimalPtBr
                        fullWidth
                        label="Peso"
                        value={superficie.peso}
                        onChange={(v) => onChange("peso", v)}
                        required
                        inputProps={{ maxLength: 12 }}
                        sx={inputSx}
                      />
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Tooltip title="Informe o passo aplicável para esteira.">
                    <TextField
                      fullWidth
                      label="Passo"
                      value={superficie.passo || ""}
                      onChange={(e) => onChange("passo", e.target.value)}
                      required
                      inputProps={{ maxLength: 50 }}
                      sx={inputSx}
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  {/* FASE 05 — 6ª validação/PO 2026-08-06: Terminal Sim/Não; o backend converte p/ BIT. */}
                  <FormControl fullWidth required>
                    <InputLabel sx={labelSx}>Terminal</InputLabel>
                    <Select
                      value={TERMINAL_OPTIONS.some((o) => o.id === superficie.terminal) ? superficie.terminal : ""}
                      label="Terminal"
                      onChange={(e) => onChange("terminal", e.target.value || undefined)}
                      sx={selectSx}
                    >
                      <MenuItem value="">
                        <em>Selecione...</em>
                      </MenuItem>
                      {TERMINAL_OPTIONS.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
